import { CommonHelperFunctions, IDisplayItem, IScreenDesign, IScreenDesignDictionary } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { KMainFunctions } from "./main-functions";
import { ScreenDesignHelper } from "./screen-design-helper";

export interface IStaticTableContext {
  tableDi: IDisplayItem;
  headerDi?: IDisplayItem;
  bodyRows: IDisplayItem[];
  rowDi: IDisplayItem;
  columnDi: IDisplayItem;
  rowIndex: number;
  columnIndex: number;
  columnCount: number;
}

const staticTableNames = {
  table: "StaticTable",
  header: "StaticTableHeader",
  row: "StaticTableRow",
  column: "StaticTableColumn"
};

const mergeMetaPropertyName = "kuikaStaticTableMergeMeta";

const getCommonStaticTableNames = () => staticTableNames;

const isStaticTableComponent = (di: IDisplayItem | undefined, targetName: string): boolean => {
  if (!di?.item?.name) return false;
  return di.item.name === targetName;
};

const getContext = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  columnKuikaId: string
): IStaticTableContext | undefined => {
  if (!columnKuikaId) return undefined;
  const columnDi = dict[parseInt(columnKuikaId, 10)];
  if (!columnDi || !isStaticTableComponent(columnDi, staticTableNames.column)) {
    return undefined;
  }

  const rowDi = columnDi.parentId ? dict[columnDi.parentId] : undefined;
  if (!rowDi || !isStaticTableComponent(rowDi, staticTableNames.row)) {
    return undefined;
  }

  const tableDi = ScreenDesignHelper.findParentTable(rowDi, dict);
  if (!tableDi || !isStaticTableComponent(tableDi, staticTableNames.table)) {
    return undefined;
  }

  const headerDi = tableDi.children?.find((child) => isStaticTableComponent(child, staticTableNames.header));
  const bodyRows = (tableDi.children ?? []).filter((child) => isStaticTableComponent(child, staticTableNames.row));
  const rowIndex = bodyRows.findIndex((row) => row.id === rowDi.id);
  if (rowIndex < 0) {
    return undefined;
  }

  const columnIndex = rowDi.children?.findIndex((col) => col.id === columnDi.id) ?? -1;
  if (columnIndex < 0) {
    return undefined;
  }

  const columnCount = bodyRows[0]?.children?.length ?? 0;

  return {
    tableDi,
    headerDi,
    bodyRows,
    rowDi,
    columnDi,
    rowIndex,
    columnIndex,
    columnCount
  };
};

const getTemplateColumn = (context: IStaticTableContext): IDisplayItem | undefined => {
  if (!context.bodyRows.length) {
    return undefined;
  }
  const referenceRow = context.bodyRows[0];
  const columnIdxSafe = Math.min(context.columnIndex, (referenceRow.children?.length ?? 1) - 1);
  return referenceRow.children?.[columnIdxSafe];
};

const addDiToDictionaryRecursive = (dict: IScreenDesignDictionary, di: IDisplayItem) => {
  dict[di.id] = di;
  di.children?.forEach((child) => addDiToDictionaryRecursive(dict, child));
};

const removeDiFromDictionaryRecursive = (dict: IScreenDesignDictionary, di: IDisplayItem | undefined) => {
  if (!di) return;
  di.children?.forEach((child) => removeDiFromDictionaryRecursive(dict, child));
  delete dict[di.id];
};

const cloneDisplayItemDeep = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  di: IDisplayItem,
  newParentId: number,
  friendlyNames?: Set<string>
): IDisplayItem => {
  const existingNames = friendlyNames || ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);

  const cloned: IDisplayItem = {
    ...di,
    item: {
      ...di.item,
      events: [],
      properties: _.cloneDeep(di.item.properties)
    },
    children: [],
    id: ScreenDesignHelper.generateUniqueDisplayItemId(dict),
    parentId: newParentId,
    friendlyName: ScreenDesignHelper.generateUniqueFriendlyName(
      di.friendlyName || di.item.name || "Component",
      existingNames
    ),
    styleObj: di.styleObj ? _.cloneDeep(di.styleObj) : undefined
  };

  ScreenDesignHelper.updateSymbolReferences(cloned, di.id, cloned.id);

  if (di.children?.length) {
    cloned.children = di.children.map((child) => cloneDisplayItemDeep(sd, dict, child, cloned.id, existingNames));
  }

  return cloned;
};

const insertRow = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  context: IStaticTableContext,
  position: "above" | "below"
): number => {
  const friendlyNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);
  const referenceRow = context.rowDi;
  const newRow = cloneDisplayItemDeep(sd, dict, referenceRow, context.tableDi.id, friendlyNames);
  newRow.children =
    referenceRow.children?.map((column) => cloneDisplayItemDeep(sd, dict, column, newRow.id, friendlyNames)) ?? [];

  addDiToDictionaryRecursive(dict, newRow);

  const insertIndex = position === "above" ? context.rowIndex : context.rowIndex + 1;
  context.tableDi.children = context.tableDi.children ?? [];
  context.tableDi.children.splice(insertIndex, 0, newRow);

  ScreenDesignHelper.validateFriendlyNames(newRow);

  return newRow.id;
};

const insertColumn = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  context: IStaticTableContext,
  position: "left" | "right"
): number | undefined => {
  const friendlyNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);

  // Step 1: Determine the logical insert position using the full occupancy grid
  const clickedSpan = getTrueGridSpan(context.bodyRows, context.rowIndex, context.columnIndex);
  if (clickedSpan.start < 0) return undefined;

  // "left": insert at the left edge of the clicked cell
  // "right": insert at the right edge of the clicked cell
  const logicalInsertCol = position === "left" ? clickedSpan.start : clickedSpan.end;

  // Step 2: Build the full occupancy grid
  const { gridMap, cells, maxLogicalCols } = buildFullOccupancyGrid(context.bodyRows);

  // Step 3: Determine width for new columns from a colspan=1 cell at the reference position
  const referenceLogical = position === "left" ? logicalInsertCol : logicalInsertCol - 1;
  let newColumnWidth = 0;
  for (const cell of cells) {
    if (cell.colspan === 1 && cell.logicalStart === referenceLogical) {
      newColumnWidth = getExistingStyleDimension(cell.column, "width");
      break;
    }
  }
  if (newColumnWidth === 0) {
    // Fallback: use any non-merged cell's width
    for (const cell of cells) {
      if (cell.colspan === 1) {
        newColumnWidth = getExistingStyleDimension(cell.column, "width");
        break;
      }
    }
  }
  if (newColumnWidth === 0) newColumnWidth = 100;

  // Find a template column for cloning structure (prefer colspan=1, rowspan=1)
  let templateColumn: IDisplayItem | undefined;
  for (const cell of cells) {
    if (cell.colspan === 1 && cell.rowspan === 1) {
      templateColumn = cell.column;
      break;
    }
  }
  if (!templateColumn) {
    templateColumn = context.bodyRows[0]?.children?.[0];
  }
  if (!templateColumn) return undefined;

  const lastCreatedColumnIds: number[] = [];
  const processedCells = new Set<IDisplayItem>();

  // Step 4: Process each body row
  for (let r = 0; r < context.bodyRows.length; r++) {
    const row = context.bodyRows[r];

    // Find what covers the logical insert position in this row
    const coveringCell = logicalInsertCol < maxLogicalCols ? gridMap[r][logicalInsertCol] : null;

    if (coveringCell && coveringCell.logicalStart < logicalInsertCol) {
      // Insert position falls INSIDE a merged cell's span → increment colspan
      if (!processedCells.has(coveringCell.column)) {
        processedCells.add(coveringCell.column);
        setSpanValue(coveringCell.column, "colspan", coveringCell.colspan + 1);
      }
    } else {
      // Insert position is at a cell boundary → insert new column
      const rowHeight = getRowHeight(row);
      const newColumn = createCleanColumnFromTemplate(
        sd,
        dict,
        templateColumn,
        row.id,
        newColumnWidth,
        rowHeight,
        friendlyNames
      );

      // Determine physical insert index
      let insertPhysIdx: number;
      if (coveringCell && coveringCell.rowIndex === r) {
        // Cell starts exactly at the insert position in this row
        insertPhysIdx = coveringCell.physicalIndex;
      } else if (coveringCell && coveringCell.rowIndex < r) {
        // Slot is covered by a rowspan cell from an earlier row.
        // Find the physical position by scanning this row's children.
        const children = row.children ?? [];
        insertPhysIdx = children.length;
        // Rebuild logical positions for this row's own physical children,
        // accounting for rowspan slots from earlier rows
        let physIdx = 0;
        let scanLogical = 0;
        while (physIdx < children.length) {
          // Skip logical slots occupied by rowspan from prior rows
          while (
            scanLogical < maxLogicalCols &&
            gridMap[r][scanLogical] !== null &&
            gridMap[r][scanLogical]!.rowIndex < r
          ) {
            scanLogical++;
          }
          if (scanLogical >= logicalInsertCol) {
            insertPhysIdx = physIdx;
            break;
          }
          scanLogical += getExistingSpanValue(children[physIdx], "colspan");
          physIdx++;
        }
      } else {
        // Past the end → append
        insertPhysIdx = (row.children ?? []).length;
      }

      row.children = row.children ?? [];
      row.children.splice(insertPhysIdx, 0, newColumn);
      addDiToDictionaryRecursive(dict, newColumn);
      ScreenDesignHelper.validateFriendlyNames(newColumn);
      lastCreatedColumnIds.push(newColumn.id);
    }
  }

  // Step 5: Handle header
  if (context.headerDi) {
    const headerChildren = context.headerDi.children ?? [];
    const headerResult = findPhysicalColumnByLogicalIndex(context.headerDi, logicalInsertCol);

    if (headerResult && headerResult.logicalStart < logicalInsertCol) {
      // Header cell spans over the insert position → increment colspan
      setSpanValue(headerResult.column, "colspan", getExistingSpanValue(headerResult.column, "colspan") + 1);
    } else {
      // Insert a new header cell
      const headerInsertIdx = headerResult ? headerResult.physicalIndex : headerChildren.length;
      const headerTemplate = headerChildren[0] ?? templateColumn;
      const headerHeight =
        headerChildren.length > 0
          ? getExistingStyleDimension(headerChildren[0], "height")
          : getRowHeight(context.bodyRows[0]);
      const newHeaderCell = createCleanColumnFromTemplate(
        sd,
        dict,
        headerTemplate,
        context.headerDi.id,
        newColumnWidth,
        headerHeight,
        friendlyNames
      );
      headerChildren.splice(headerInsertIdx, 0, newHeaderCell);
      addDiToDictionaryRecursive(dict, newHeaderCell);
      ScreenDesignHelper.validateFriendlyNames(newHeaderCell);
    }
    context.headerDi.children = headerChildren;
  }

  // Step 6: Recalculate all merged cell dimensions
  recalculateAllMergedCellDimensions(context.tableDi, dict);

  return lastCreatedColumnIds[lastCreatedColumnIds.length - 1];
};

const splitCell = (sd: IScreenDesign, dict: IScreenDesignDictionary, context: IStaticTableContext): number => {
  const friendlyNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);
  const column = context.columnDi;
  const children = column.children ?? [];
  if (!children.length) {
    return column.id;
  }
  const duplicatedChildren = children.map((child) => cloneDisplayItemDeep(sd, dict, child, column.id, friendlyNames));
  duplicatedChildren.forEach((child) => {
    addDiToDictionaryRecursive(dict, child);
    ScreenDesignHelper.validateFriendlyNames(child);
  });
  column.children = [...children, ...duplicatedChildren];
  return column.id;
};

const deleteRow = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  context: IStaticTableContext
): number | undefined => {
  if (context.bodyRows.length <= 1) {
    KMainFunctions.displayWarningNotification("Static table must contain at least one row.");
    return undefined;
  }
  removeDiFromDictionaryRecursive(dict, context.rowDi);
  context.tableDi.children = (context.tableDi.children ?? []).filter((child) => child.id !== context.rowDi.id);
  const nextFocusRow = context.tableDi.children?.find((child) => isStaticTableComponent(child, staticTableNames.row));
  return nextFocusRow?.id;
};

const deleteColumn = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  context: IStaticTableContext
): number | undefined => {
  if (context.columnCount <= 1) {
    KMainFunctions.displayWarningNotification("Static table must contain at least one column.");
    return undefined;
  }

  if (context.headerDi) {
    const headerChildren = context.headerDi.children ?? [];
    const removedHeader = headerChildren.splice(context.columnIndex, 1)[0];
    removeDiFromDictionaryRecursive(dict, removedHeader);
    context.headerDi.children = headerChildren;
  }

  context.bodyRows.forEach((row) => {
    const removedColumn = row.children?.splice(context.columnIndex, 1)[0];
    removeDiFromDictionaryRecursive(dict, removedColumn);
  });

  const focusColumn = context.rowDi.children?.[Math.max(0, context.columnIndex - 1)];
  return focusColumn?.id;
};

const deleteCellContent = (sd: IScreenDesign, dict: IScreenDesignDictionary, context: IStaticTableContext): number => {
  const column = context.columnDi;
  column.children?.forEach((child) => removeDiFromDictionaryRecursive(dict, child));
  column.children = [];
  return column.id;
};

const markMergeMeta = (column: IDisplayItem, meta: any) => {
  if (!column.item) return;
  (column.item as any).meta = { ...((column.item as any).meta ?? {}), [mergeMetaPropertyName]: meta };
};

const clearMergeMeta = (column: IDisplayItem) => {
  if (!(column.item as any).meta) return;
  delete (column.item as any).meta[mergeMetaPropertyName];
};

const getExistingSpanValue = (column: IDisplayItem, spanType: "colspan" | "rowspan"): number => {
  const spanProperty = column.item.properties.find((prop) => prop.name === spanType);
  if (!spanProperty?.symbol?.fixedValue) return 1;
  const spanValue = parseInt(spanProperty.symbol.fixedValue, 10);
  return isNaN(spanValue) || spanValue < 1 ? 1 : spanValue;
};

const getExistingStyleDimension = (column: IDisplayItem, dimensionType: "width" | "height"): number => {
  const dimensionValue = column.styleObj?.position?.[dimensionType];
  if (dimensionValue === undefined || dimensionValue === null) return 0;
  const numValue = typeof dimensionValue === "number" ? dimensionValue : parseInt(String(dimensionValue), 10);
  return isNaN(numValue) || numValue < 0 ? 0 : numValue;
};

const setStyleDimension = (column: IDisplayItem, dimensionType: "width" | "height", value: number) => {
  if (value <= 0) return;

  if (!column.styleObj) {
    column.styleObj = {} as any;
  }
  if (!column.styleObj!.position) {
    column.styleObj!.position = {} as any;
  }

  (column.styleObj!.position as any)[dimensionType] = value;
  (column.styleObj!.position as any)[`${dimensionType}Style`] = "px";
};

/**
 * Sets (or creates) a colspan/rowspan property on a column.
 * Removes the property if value <= 1 (default).
 */
const setSpanValue = (column: IDisplayItem, spanType: "colspan" | "rowspan", value: number): void => {
  const propertyIndex = column.item.properties.findIndex((prop) => prop.name === spanType);
  if (value <= 1) {
    if (propertyIndex !== -1) {
      column.item.properties[propertyIndex].symbol = {
        ...column.item.properties[propertyIndex].symbol,
        category: 4,
        fixedValue: ""
      };
    }
    return;
  }
  if (propertyIndex !== -1) {
    column.item.properties[propertyIndex].symbol = {
      ...column.item.properties[propertyIndex].symbol,
      category: 4,
      fixedValue: value.toString()
    };
  } else {
    column.item.properties.push({
      name: spanType,
      symbol: {
        category: 4,
        fixedValue: value.toString(),
        label: "Value",
        description: "Fixed Value"
      },
      selectedType: 9,
      reactNodeValue: ""
    } as any);
  }
};

interface IOccupancyCell {
  column: IDisplayItem;
  physicalIndex: number;
  rowIndex: number;
  logicalStart: number;
  logicalEnd: number;
  colspan: number;
  rowspan: number;
}

/**
 * Builds a complete grid occupancy map for the table body.
 * Returns gridMap[rowIndex][logicalCol] telling which physical cell covers each slot,
 * plus a flat list of all cells with their logical positions.
 */
const buildFullOccupancyGrid = (
  bodyRows: IDisplayItem[]
): {
  gridMap: (IOccupancyCell | null)[][];
  cells: IOccupancyCell[];
  maxLogicalCols: number;
} => {
  let maxLogicalCols = 0;
  for (const row of bodyRows) {
    let cols = 0;
    for (const child of row.children ?? []) {
      cols += getExistingSpanValue(child, "colspan");
    }
    if (cols > maxLogicalCols) maxLogicalCols = cols;
  }
  maxLogicalCols += bodyRows.length;

  const gridMap: (IOccupancyCell | null)[][] = bodyRows.map(() => new Array(maxLogicalCols).fill(null));
  const cells: IOccupancyCell[] = [];

  for (let r = 0; r < bodyRows.length; r++) {
    const children = bodyRows[r].children ?? [];
    let logicalCol = 0;

    for (let p = 0; p < children.length; p++) {
      while (logicalCol < maxLogicalCols && gridMap[r][logicalCol] !== null) {
        logicalCol++;
      }

      const colspan = getExistingSpanValue(children[p], "colspan");
      const rowspan = getExistingSpanValue(children[p], "rowspan");

      const cell: IOccupancyCell = {
        column: children[p],
        physicalIndex: p,
        rowIndex: r,
        logicalStart: logicalCol,
        logicalEnd: logicalCol + colspan,
        colspan,
        rowspan
      };
      cells.push(cell);

      for (let dr = 0; dr < rowspan && r + dr < bodyRows.length; dr++) {
        for (let dc = 0; dc < colspan && logicalCol + dc < maxLogicalCols; dc++) {
          gridMap[r + dr][logicalCol + dc] = cell;
        }
      }

      logicalCol += colspan;
    }
  }

  return { gridMap, cells, maxLogicalCols };
};

/**
 * Creates a new column (colspan=1, rowspan=1) cloned from a template,
 * stripping any merge properties, clearing merge metadata, and setting the given width.
 */
const createCleanColumnFromTemplate = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  template: IDisplayItem,
  parentId: number,
  width: number,
  height: number,
  friendlyNames: Set<string>
): IDisplayItem => {
  const newColumn = cloneDisplayItemDeep(sd, dict, template, parentId, friendlyNames);

  // Reset colspan to default (keep property visible in property grid)
  const colspanIdx = newColumn.item.properties.findIndex((p) => p.name === "colspan");
  if (colspanIdx !== -1) {
    newColumn.item.properties[colspanIdx].symbol = {
      ...newColumn.item.properties[colspanIdx].symbol,
      category: 4,
      fixedValue: ""
    };
  }

  // Reset rowspan to default (keep property visible in property grid)
  const rowspanIdx = newColumn.item.properties.findIndex((p) => p.name === "rowspan");
  if (rowspanIdx !== -1) {
    newColumn.item.properties[rowspanIdx].symbol = {
      ...newColumn.item.properties[rowspanIdx].symbol,
      category: 4,
      fixedValue: ""
    };
  }

  clearMergeMeta(newColumn);
  newColumn.children = [];

  if (width > 0) {
    setStyleDimension(newColumn, "width", width);
    if (newColumn.absolutePositionInfo) newColumn.absolutePositionInfo.width = width;
    if (newColumn.fixedPositionInfo) newColumn.fixedPositionInfo.width = width;
  }
  if (height > 0) {
    setStyleDimension(newColumn, "height", height);
    if (newColumn.absolutePositionInfo) newColumn.absolutePositionInfo.height = height;
    if (newColumn.fixedPositionInfo) newColumn.fixedPositionInfo.height = height;
  }

  return newColumn;
};

/**
 * Gets the height of a row from its first rowspan=1 cell.
 */
const getRowHeight = (row: IDisplayItem): number => {
  for (const child of row.children ?? []) {
    if (getExistingSpanValue(child, "rowspan") === 1) {
      return getExistingStyleDimension(child, "height");
    }
  }
  const firstChild = (row.children ?? [])[0];
  return firstChild ? getExistingStyleDimension(firstChild, "height") || 40 : 40;
};

/**
 * Computes the logical (grid-model) column index for a column at a given
 * physical position within a row's children array.
 * Each physical column occupies `colspan` logical slots (default 1).
 */
const getLogicalColumnIndex = (row: IDisplayItem, physicalIndex: number): number => {
  const children = row.children ?? [];
  let logicalIndex = 0;
  for (let i = 0; i < physicalIndex && i < children.length; i++) {
    logicalIndex += getExistingSpanValue(children[i], "colspan");
  }
  return logicalIndex;
};

/**
 * Returns the logical column range { start, end (exclusive) } for a column
 * at the given physical index.
 */
const getLogicalColumnSpan = (row: IDisplayItem, physicalIndex: number): { start: number; end: number } => {
  const start = getLogicalColumnIndex(row, physicalIndex);
  const children = row.children ?? [];
  const colspan = physicalIndex < children.length ? getExistingSpanValue(children[physicalIndex], "colspan") : 1;
  return { start, end: start + colspan };
};

/**
 * Given a row and a target logical column index, finds the physical column
 * whose logical span includes that index.
 */
const findPhysicalColumnByLogicalIndex = (
  row: IDisplayItem,
  targetLogicalIndex: number
):
  | {
      column: IDisplayItem;
      physicalIndex: number;
      logicalStart: number;
      logicalEnd: number;
    }
  | undefined => {
  const children = row.children ?? [];
  let logicalIndex = 0;

  for (let i = 0; i < children.length; i++) {
    const colspan = getExistingSpanValue(children[i], "colspan");
    const logicalEnd = logicalIndex + colspan;

    if (targetLogicalIndex >= logicalIndex && targetLogicalIndex < logicalEnd) {
      return {
        column: children[i],
        physicalIndex: i,
        logicalStart: logicalIndex,
        logicalEnd
      };
    }

    logicalIndex = logicalEnd;
  }

  return undefined;
};

/**
 * Calculates the total width of a logical column range by summing
 * widths of individual columns from a reference row.
 * Used to recalculate a merged cell's width from its constituent columns.
 */
const sumWidthsForLogicalRange = (referenceRow: IDisplayItem, logicalStart: number, logicalEnd: number): number => {
  const children = referenceRow.children ?? [];
  let totalWidth = 0;
  let logicalIndex = 0;

  for (let i = 0; i < children.length; i++) {
    const colspan = getExistingSpanValue(children[i], "colspan");
    const colLogicalEnd = logicalIndex + colspan;

    // Check if this column overlaps with the target range
    if (colLogicalEnd > logicalStart && logicalIndex < logicalEnd) {
      totalWidth += getExistingStyleDimension(children[i], "width");
    }

    logicalIndex = colLogicalEnd;
    if (logicalIndex >= logicalEnd) break;
  }

  return totalWidth;
};

/**
 * Calculates the total height of a logical row range by summing
 * heights of individual columns from a reference column position across rows.
 * Used to recalculate a vertically merged cell's height from its constituent rows.
 */
const sumHeightsForRowRange = (
  bodyRows: IDisplayItem[],
  startRowIndex: number,
  rowspan: number,
  logicalColumnIndex: number
): number => {
  let totalHeight = 0;

  for (let i = startRowIndex; i < startRowIndex + rowspan && i < bodyRows.length; i++) {
    const row = bodyRows[i];
    const result = findPhysicalColumnByLogicalIndex(row, logicalColumnIndex);
    if (result) {
      const colRowspan = getExistingSpanValue(result.column, "rowspan");
      // Only sum height from non-merged (rowspan=1) columns to avoid double counting
      if (colRowspan === 1) {
        totalHeight += getExistingStyleDimension(result.column, "height");
      }
    }
  }

  return totalHeight;
};

/**
 * Recalculates all merged cell dimensions in a StaticTable.
 * Non-merged cells (colspan=1, rowspan=1) are the source of truth.
 * Merged cells derive their width/height from the base grid.
 */
const recalculateAllMergedCellDimensions = (tableDi: IDisplayItem, dict: IScreenDesignDictionary): void => {
  const bodyRows = (tableDi.children ?? []).filter((child) => isStaticTableComponent(child, staticTableNames.row));
  if (bodyRows.length === 0) return;

  // Build row heights from rowspan=1 cells (any rowspan=1 cell defines its row's height)
  const rowHeights: number[] = bodyRows.map((row) => {
    const children = row.children ?? [];
    for (let i = 0; i < children.length; i++) {
      if (getExistingSpanValue(children[i], "rowspan") === 1) {
        return getExistingStyleDimension(children[i], "height");
      }
    }
    return 0;
  });

  // Build logical column widths from colspan=1 cells across all rows
  const logicalColumnWidths: Map<number, number> = new Map();
  let maxLogicalColumns = 0;
  for (const row of bodyRows) {
    const children = row.children ?? [];
    let logicalIndex = 0;
    for (let i = 0; i < children.length; i++) {
      const colspan = getExistingSpanValue(children[i], "colspan");
      if (colspan === 1) {
        logicalColumnWidths.set(logicalIndex, getExistingStyleDimension(children[i], "width"));
      }
      logicalIndex += colspan;
    }
    if (logicalIndex > maxLogicalColumns) maxLogicalColumns = logicalIndex;
  }

  // Recalculate all merged cells
  for (let rowIdx = 0; rowIdx < bodyRows.length; rowIdx++) {
    const row = bodyRows[rowIdx];
    const children = row.children ?? [];
    let logicalIndex = 0;

    for (let i = 0; i < children.length; i++) {
      const cell = children[i];
      const colspan = getExistingSpanValue(cell, "colspan");
      const rowspan = getExistingSpanValue(cell, "rowspan");

      if (colspan > 1) {
        // Clamp to grid bounds to handle overflow
        const colEnd = Math.min(logicalIndex + colspan, maxLogicalColumns);
        let newWidth = 0;
        for (let c = logicalIndex; c < colEnd; c++) {
          newWidth += logicalColumnWidths.get(c) ?? 0;
        }
        if (newWidth > 0) {
          setStyleDimension(cell, "width", newWidth);
          if (cell.absolutePositionInfo) cell.absolutePositionInfo.width = newWidth;
          if (cell.fixedPositionInfo) cell.fixedPositionInfo.width = newWidth;
        }
      }

      if (rowspan > 1) {
        // Sum row heights for this cell's span (already clamped by rowHeights.length)
        let newHeight = 0;
        for (let r = rowIdx; r < rowIdx + rowspan && r < rowHeights.length; r++) {
          newHeight += rowHeights[r];
        }
        if (newHeight > 0) {
          setStyleDimension(cell, "height", newHeight);
          if (cell.absolutePositionInfo) cell.absolutePositionInfo.height = newHeight;
          if (cell.fixedPositionInfo) cell.fixedPositionInfo.height = newHeight;
        }
      }

      logicalIndex += colspan;
    }
  }
};

const handleMergeColumns = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  context: IStaticTableContext,
  targetColumnIds: number[]
): number | undefined => {
  if (targetColumnIds.length <= 1) {
    KMainFunctions.displayWarningNotification("Please select at least two columns to merge.");
    return undefined;
  }

  const orderedIds = [...targetColumnIds].sort((a, b) => a - b);
  const primaryColumnId = orderedIds[0];
  const primaryColumn = dict[primaryColumnId];
  if (!primaryColumn) {
    return undefined;
  }

  const mergedChildren: IDisplayItem[] = [];
  const removedColumns: IDisplayItem[] = [];
  let totalColspan = getExistingSpanValue(primaryColumn, "colspan");
  let totalWidth = getExistingStyleDimension(primaryColumn, "width");

  orderedIds.slice(1).forEach((columnId) => {
    const columnDi = dict[columnId];
    if (!columnDi) return;
    totalColspan += getExistingSpanValue(columnDi, "colspan");
    totalWidth += getExistingStyleDimension(columnDi, "width");
    columnDi.children?.forEach((child) => {
      child.parentId = primaryColumn.id;
      mergedChildren.push(child);
    });
    removedColumns.push(columnDi);
  });

  primaryColumn.children = [...(primaryColumn.children ?? []), ...mergedChildren];
  markMergeMeta(primaryColumn, {
    removedColumnIds: removedColumns.map((col) => col.id)
  });

  const colspanValue = totalColspan;
  const colspanPropertyIndex = primaryColumn.item.properties.findIndex((prop) => prop.name === "colspan");
  if (colspanPropertyIndex !== -1) {
    primaryColumn.item.properties[colspanPropertyIndex].symbol = {
      ...primaryColumn.item.properties[colspanPropertyIndex].symbol,
      category: 4,
      fixedValue: colspanValue.toString()
    };
  } else {
    const newColspanProperty = {
      name: "colspan",
      symbol: {
        category: 4,
        fixedValue: colspanValue.toString(),
        label: "Value",
        description: "Fixed Value"
      },
      selectedType: 9,
      reactNodeValue: ""
    } as any;
    primaryColumn.item.properties.push(newColspanProperty);
  }

  // Set the accumulated width
  setStyleDimension(primaryColumn, "width", totalWidth);

  removedColumns.forEach((column) => {
    const parentRow = column.parentId ? dict[column.parentId] : undefined;
    if (parentRow) {
      parentRow.children = parentRow.children?.filter((child) => child.id !== column.id);
    }
    removeDiFromDictionaryRecursive(dict, column);
  });

  return primaryColumn.id;
};

const handleUnmergeColumns = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  context: IStaticTableContext
): number => {
  const friendlyNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);
  const column = context.columnDi;
  const currentColspan = getExistingSpanValue(column, "colspan");
  const currentRowspan = getExistingSpanValue(column, "rowspan");

  if (currentColspan <= 1 && currentRowspan <= 1) {
    KMainFunctions.displayWarningNotification("Selected cell is not merged.");
    return column.id;
  }

  if (currentRowspan > 1 && currentColspan > 1) {
    KMainFunctions.displayWarningNotification(
      "Cell has both colspan and rowspan. Please unmerge one direction at a time. Unmerging horizontally first."
    );
  }

  if (currentColspan > 1) {
    const currentWidth = getExistingStyleDimension(column, "width");
    const newWidth = currentWidth > 0 ? currentWidth / currentColspan : 0;

    const allChildren = column.children ?? [];
    const childrenPerColumn = Math.ceil(allChildren.length / currentColspan);

    const colspanPropertyIndex = column.item.properties.findIndex((prop) => prop.name === "colspan");
    if (colspanPropertyIndex !== -1) {
      column.item.properties[colspanPropertyIndex].symbol = {
        ...column.item.properties[colspanPropertyIndex].symbol,
        category: 4,
        fixedValue: "1"
      };
    }

    if (newWidth > 0) {
      setStyleDimension(column, "width", newWidth);
    }

    const newColumns: IDisplayItem[] = [];

    for (let i = 1; i < currentColspan; i++) {
      const newId = ScreenDesignHelper.generateUniqueDisplayItemId(dict);
      const clone: IDisplayItem = {
        id: newId,
        parentId: context.rowDi.id,
        item: _.cloneDeep(column.item),
        children: [],
        friendlyName: ScreenDesignHelper.generateUniqueFriendlyName(column.item.name || "Component", friendlyNames),
        styleObj: _.cloneDeep(column.styleObj) || {}
      };

      if (newWidth > 0) {
        setStyleDimension(clone, "width", newWidth);
      }

      if (currentRowspan > 1) {
        const rowspanPropertyIndex = clone.item.properties.findIndex((prop) => prop.name === "rowspan");
        if (rowspanPropertyIndex !== -1) {
          clone.item.properties[rowspanPropertyIndex].symbol = {
            ...clone.item.properties[rowspanPropertyIndex].symbol,
            category: 4,
            fixedValue: currentRowspan.toString()
          };
        } else {
          const newRowspanProperty = {
            name: "rowspan",
            symbol: {
              category: 4,
              fixedValue: currentRowspan.toString(),
              label: "Value",
              description: "Fixed Value"
            },
            selectedType: 9,
            reactNodeValue: ""
          } as any;
          clone.item.properties.push(newRowspanProperty);
        }
      }

      newColumns.push(clone);
      context.rowDi.children?.splice(context.columnIndex + i, 0, clone);
      addDiToDictionaryRecursive(dict, clone);
    }

    allChildren.forEach((child, index) => {
      const targetColumnIndex = Math.floor(index / childrenPerColumn);
      if (targetColumnIndex === 0) {
        return;
      }

      const targetColumn = newColumns[targetColumnIndex - 1];
      if (targetColumn) {
        child.parentId = targetColumn.id;
        targetColumn.children = targetColumn.children ?? [];
        targetColumn.children.push(child);
      }
    });

    const remainingChildren = allChildren.slice(0, childrenPerColumn);
    column.children = remainingChildren;

    clearMergeMeta(column);

    return column.id;
  }

  if (currentRowspan > 1) {
    return handleUnmergeRows(sd, dict, context);
  }

  return column.id;
};

const handleMergeColumnsVertically = (
  sd: IScreenDesign,
  dict: IScreenDesignDictionary,
  selectedColumns: Array<{ columnId: number; rowIndex: number; columnIndex: number }>
): number | undefined => {
  if (selectedColumns.length <= 1) {
    KMainFunctions.displayWarningNotification("Please select at least two columns to merge.");
    return undefined;
  }

  const orderedColumns = [...selectedColumns].sort((a, b) => a.rowIndex - b.rowIndex);
  const primaryColumnId = orderedColumns[0].columnId;
  const primaryColumn = dict[primaryColumnId];
  if (!primaryColumn) {
    return undefined;
  }

  const mergedChildren: IDisplayItem[] = [];
  const removedColumns: IDisplayItem[] = [];
  let totalRowspan = getExistingSpanValue(primaryColumn, "rowspan");
  let totalHeight = getExistingStyleDimension(primaryColumn, "height");

  const primaryColspan = getExistingSpanValue(primaryColumn, "colspan");

  let colspanMismatch = false;

  orderedColumns.slice(1).forEach((colInfo) => {
    const columnDi = dict[colInfo.columnId];
    if (!columnDi) return;

    const currentColspan = getExistingSpanValue(columnDi, "colspan");
    if (currentColspan !== primaryColspan) {
      colspanMismatch = true;
      return;
    }

    totalRowspan += getExistingSpanValue(columnDi, "rowspan");
    totalHeight += getExistingStyleDimension(columnDi, "height");
    columnDi.children?.forEach((child) => {
      child.parentId = primaryColumn.id;
      mergedChildren.push(child);
    });
    removedColumns.push(columnDi);
  });

  if (colspanMismatch) {
    KMainFunctions.displayWarningNotification(
      "Cannot merge cells vertically with different colspan values. All cells must have the same colspan."
    );
    return undefined;
  }

  if (removedColumns.length !== orderedColumns.length - 1) {
    return undefined;
  }

  primaryColumn.children = [...(primaryColumn.children ?? []), ...mergedChildren];
  markMergeMeta(primaryColumn, {
    removedColumnIds: removedColumns.map((col) => col.id),
    mergeType: "vertical"
  });

  const rowspanValue = totalRowspan;
  const rowspanPropertyIndex = primaryColumn.item.properties.findIndex((prop) => prop.name === "rowspan");
  if (rowspanPropertyIndex !== -1) {
    primaryColumn.item.properties[rowspanPropertyIndex].symbol = {
      ...primaryColumn.item.properties[rowspanPropertyIndex].symbol,
      category: 4,
      fixedValue: rowspanValue.toString()
    };
  } else {
    const newRowspanProperty = {
      name: "rowspan",
      symbol: {
        category: 4,
        fixedValue: rowspanValue.toString(),
        label: "Value",
        description: "Fixed Value"
      },
      selectedType: 9,
      reactNodeValue: ""
    } as any;
    primaryColumn.item.properties.push(newRowspanProperty);
  }

  setStyleDimension(primaryColumn, "height", totalHeight);

  removedColumns.forEach((column) => {
    const parentRow = column.parentId ? dict[column.parentId] : undefined;
    if (parentRow) {
      parentRow.children = parentRow.children?.filter((child) => child.id !== column.id);
    }
    removeDiFromDictionaryRecursive(dict, column);
  });

  return primaryColumn.id;
};

const handleUnmergeRows = (sd: IScreenDesign, dict: IScreenDesignDictionary, context: IStaticTableContext): number => {
  const friendlyNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);
  const column = context.columnDi;
  const currentRowspan = getExistingSpanValue(column, "rowspan");

  if (currentRowspan <= 1) {
    KMainFunctions.displayWarningNotification("Selected column cannot be unmerged (rowspan is 1).");
    return column.id;
  }

  const currentHeight = getExistingStyleDimension(column, "height");
  const newHeight = currentHeight > 0 ? currentHeight / currentRowspan : 0;
  const currentColspan = getExistingSpanValue(column, "colspan");

  const allChildren = column.children ?? [];
  const childrenPerRow = Math.ceil(allChildren.length / currentRowspan);

  const rowspanPropertyIndex = column.item.properties.findIndex((prop) => prop.name === "rowspan");
  if (rowspanPropertyIndex !== -1) {
    column.item.properties[rowspanPropertyIndex].symbol = {
      ...column.item.properties[rowspanPropertyIndex].symbol,
      category: 4,
      fixedValue: "1"
    };
  }

  if (newHeight > 0) {
    setStyleDimension(column, "height", newHeight);
  }

  const newColumns: IDisplayItem[] = [];

  for (let i = 1; i < currentRowspan; i++) {
    const targetRowIndex = context.rowIndex + i;
    if (targetRowIndex >= context.bodyRows.length) {
      const newRowId = insertRow(sd, dict, context, "below");
      const newRow = dict[newRowId];
      context.bodyRows.push(newRow);
    }

    const targetRow = context.bodyRows[targetRowIndex];
    if (!targetRow) continue;

    const newId = ScreenDesignHelper.generateUniqueDisplayItemId(dict);
    const clone: IDisplayItem = {
      id: newId,
      parentId: targetRow.id,
      item: _.cloneDeep(column.item),
      children: [],
      friendlyName: ScreenDesignHelper.generateUniqueFriendlyName(column.item.name || "Component", friendlyNames),
      styleObj: _.cloneDeep(column.styleObj) || {}
    };

    if (newHeight > 0) {
      setStyleDimension(clone, "height", newHeight);
    }

    if (currentColspan > 1) {
      const colspanPropertyIndex = clone.item.properties.findIndex((prop) => prop.name === "colspan");
      if (colspanPropertyIndex !== -1) {
        clone.item.properties[colspanPropertyIndex].symbol = {
          ...clone.item.properties[colspanPropertyIndex].symbol,
          category: 4,
          fixedValue: currentColspan.toString()
        };
      } else {
        const newColspanProperty = {
          name: "colspan",
          symbol: {
            category: 4,
            fixedValue: currentColspan.toString(),
            label: "Value",
            description: "Fixed Value"
          },
          selectedType: 9,
          reactNodeValue: ""
        } as any;
        clone.item.properties.push(newColspanProperty);
      }
    }

    newColumns.push(clone);

    if (!targetRow.children) {
      targetRow.children = [];
    }

    if (context.columnIndex < targetRow.children.length) {
      targetRow.children.splice(context.columnIndex, 0, clone);
    } else {
      targetRow.children.push(clone);
    }

    addDiToDictionaryRecursive(dict, clone);
  }

  allChildren.forEach((child, index) => {
    const targetRowIndex = Math.floor(index / childrenPerRow);
    if (targetRowIndex === 0) {
      return;
    }

    const targetColumn = newColumns[targetRowIndex - 1];
    if (targetColumn) {
      child.parentId = targetColumn.id;
      targetColumn.children = targetColumn.children ?? [];
      targetColumn.children.push(child);
    }
  });

  const remainingChildren = allChildren.slice(0, childrenPerRow);
  column.children = remainingChildren;

  clearMergeMeta(column);

  return column.id;
};

/**
 * Builds a grid occupancy map for the table and returns the true logical
 * column range { start, end } for a given cell, accounting for both
 * colspan and rowspan coverage from cells in earlier rows.
 *
 * Without this, `getLogicalColumnSpan` only sums colspans within a single
 * row and cannot see that a column slot is already occupied by a rowspan
 * cell from a previous row.
 */
const getTrueGridSpan = (
  bodyRows: IDisplayItem[],
  targetRowIndex: number,
  targetPhysicalIndex: number
): { start: number; end: number } => {
  // Determine the maximum number of logical columns
  let maxLogicalCols = 0;
  for (const row of bodyRows) {
    let cols = 0;
    for (const child of row.children ?? []) {
      cols += getExistingSpanValue(child, "colspan");
    }
    if (cols > maxLogicalCols) maxLogicalCols = cols;
  }
  // Add extra cols to account for rowspan shifts
  maxLogicalCols += bodyRows.length;

  // occupied[row][logicalCol] = true if that grid slot is taken
  const occupied: boolean[][] = bodyRows.map(() => new Array(maxLogicalCols).fill(false));

  for (let r = 0; r < bodyRows.length; r++) {
    const children = bodyRows[r].children ?? [];
    let logicalCol = 0;

    for (let p = 0; p < children.length; p++) {
      // Skip occupied slots (filled by rowspan from earlier rows)
      while (logicalCol < maxLogicalCols && occupied[r][logicalCol]) {
        logicalCol++;
      }

      const colspan = getExistingSpanValue(children[p], "colspan");
      const rowspan = getExistingSpanValue(children[p], "rowspan");

      if (r === targetRowIndex && p === targetPhysicalIndex) {
        return { start: logicalCol, end: logicalCol + colspan };
      }

      // Mark grid slots as occupied
      for (let dr = 0; dr < rowspan && r + dr < bodyRows.length; dr++) {
        for (let dc = 0; dc < colspan && logicalCol + dc < maxLogicalCols; dc++) {
          occupied[r + dr][logicalCol + dc] = true;
        }
      }

      logicalCol += colspan;
    }
  }

  // Fallback – should not happen
  return { start: -1, end: -1 };
};

/**
 * Determines the merge direction for a set of selected column positions.
 * Uses the grid occupancy map to correctly identify logical column positions
 * even when physical indices have shifted due to prior merges / rowspans.
 *
 * Returns:
 *  - "horizontal" : all cells in the same row
 *  - "vertical"   : cells in different rows but occupying the same logical column range
 *  - "invalid"    : cells don't form a valid merge (different rows AND different columns)
 */
const determineMergeDirection = (
  bodyRows: IDisplayItem[],
  columnPositions: Array<{ columnId: number; rowIndex: number; columnIndex: number }>
): "horizontal" | "vertical" | "invalid" => {
  if (columnPositions.length < 2) return "invalid";

  const areInDifferentRows = columnPositions.some((col) => col.rowIndex !== columnPositions[0].rowIndex);

  if (!areInDifferentRows) return "horizontal";

  // Cells are in different rows – check if they occupy the same logical columns
  const spans = columnPositions.map((col) => getTrueGridSpan(bodyRows, col.rowIndex, col.columnIndex));

  const areInSameColumn = spans.every((span) => span.start === spans[0].start && span.end === spans[0].end);

  return areInSameColumn ? "vertical" : "invalid";
};

/**
 * KOA-18016 load-time heal: repair StaticTables whose row/header bands contain a non-StaticTableColumn
 * child. Such an orphan (e.g. an Icon/Label that got dropped before/after a column and landed directly
 * under the StaticTableRow, with no StaticTableColumn wrapper) makes the whole table's columns
 * unselectable and blocks further drops, because StaticTableRow/StaticTableHeader render no canvas
 * wrapper and the grid math treats the orphan as a phantom column.
 *
 * Each orphan is wrapped in a fresh StaticTableColumn cloned from an existing column in the same table,
 * replacing it in its own `children[idx]` slot. The orphan is MOVED by reference — its id, events and
 * subtree survive, so external symbol references pointing at it stay valid. (The previously-corrupted
 * row may end up with one more column than its siblings, since the orphan was an extra child; the table
 * is fully selectable/editable again and the user can delete that extra column — the goal here is to
 * un-break the table, not to reconstruct the intended layout.) Healthy tables are left untouched. A
 * degenerate table with no StaticTableColumn anywhere (nothing to clone a wrapper from) is skipped.
 *
 * Scope: StaticTable only. ReportTable's orphan case is prevented at the drop sources (drag-drop-helper
 * redirect + the ReportTableRow ∈ TABLE_TARGETS validation rule); healing an already-corrupted
 * ReportTable is deferred because its data-bound columns and lack of a merge grid need separate handling.
 *
 * Runs once on project load; returns true if anything was repaired (so the caller persists the fix).
 */
const normalizeStaticTableRows = (sd: IScreenDesign): boolean => {
  if (!sd?.displayItems?.length) return false;

  const dict = CommonHelperFunctions.createScreenDesignDict(sd);
  const friendlyNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);
  let repairedAny = false;

  for (const tableDi of Object.values(dict)) {
    if (!isStaticTableComponent(tableDi, staticTableNames.table)) continue;

    const bands = (tableDi.children ?? []).filter(
      (child) =>
        isStaticTableComponent(child, staticTableNames.row) || isStaticTableComponent(child, staticTableNames.header)
    );

    // A template to clone the wrapper column from: any existing StaticTableColumn in this table.
    let templateColumn: IDisplayItem | undefined;
    for (const band of bands) {
      const existingColumn = band.children?.find((child) => isStaticTableComponent(child, staticTableNames.column));
      if (existingColumn) {
        templateColumn = existingColumn;
        break;
      }
    }
    // Degenerate table with no column to clone from — leave it untouched rather than fabricate one.
    if (!templateColumn) continue;

    let tableRepaired = false;
    for (const band of bands) {
      const children = band.children;
      if (!children?.length) continue;
      for (let idx = 0; idx < children.length; idx++) {
        const orphan = children[idx];
        if (isStaticTableComponent(orphan, staticTableNames.column)) continue;

        // Wrap the orphan in a fresh column, replacing it in its own slot. Passing width/height 0 keeps
        // the cloned template's dimensions, matching sibling columns.
        const wrapper = createCleanColumnFromTemplate(sd, dict, templateColumn, band.id, 0, 0, friendlyNames);
        orphan.parentId = wrapper.id;
        wrapper.children = [orphan];
        children[idx] = wrapper;
        addDiToDictionaryRecursive(dict, wrapper);
        repairedAny = true;
        tableRepaired = true;
      }
    }

    if (tableRepaired) {
      recalculateAllMergedCellDimensions(tableDi, dict);
    }
  }

  return repairedAny;
};

export const StaticTableHelper = {
  getContext,
  normalizeStaticTableRows,
  insertRow,
  insertColumn,
  splitCell,
  deleteRow,
  deleteColumn,
  deleteCellContent,
  handleMergeColumns,
  handleMergeColumnsVertically,
  handleUnmergeColumns,
  handleUnmergeRows,
  getCommonStaticTableNames,
  getExistingSpanValue,
  getLogicalColumnIndex,
  getLogicalColumnSpan,
  findPhysicalColumnByLogicalIndex,
  sumWidthsForLogicalRange,
  sumHeightsForRowRange,
  recalculateAllMergedCellDimensions,
  getTrueGridSpan,
  determineMergeDirection
};
